import type { Member, MemberPublic, PostEnriched } from '@/lib/db/types';
import { gamesWithSettlement, settledLiveBets } from '@/lib/feed/post-settlement';

export interface RoundRecap {
  subject: string;
  body: string;
}

function scoreLabel(score: number, par: number): string {
  const d = score - par;
  if (d === 0) return 'even par';
  return d > 0 ? `${d} over par` : `${Math.abs(d)} under par`;
}

function holeHighlights(post: PostEnriched): string {
  const holes = post.round?.round_holes;
  const courseHoles = post.round?.course_holes;
  if (!holes?.length || !courseHoles?.length) return '';

  const items: string[] = [];
  holes.forEach((h) => {
    const par = courseHoles.find((ch) => ch.hole_num === h.hole_num)?.par ?? 4;
    const d = h.strokes - par;
    if (d <= -2) items.push(`eagle on ${h.hole_num}`);
    else if (d === -1) items.push(`birdie on ${h.hole_num}`);
    else if (d >= 3) items.push(`triple on ${h.hole_num}`);
  });
  return items.slice(0, 4).join(', ');
}

/** Templated round recap (no live AI — Phase 3 can swap in Anthropic). */
export function buildRoundRecap(
  post: PostEnriched,
  me: Member,
  memberMap: Record<string, MemberPublic>,
): RoundRecap {
  const player = me.name || 'A member';
  const gross = post.round?.gross_total ?? 0;
  const par = post.round?.course?.par ?? 72;
  const courseName = post.round?.course?.name || post.metadata?.courseName || 'the club';
  const diffStr = scoreLabel(gross, par);
  const highlights = holeHighlights(post);

  const taggedNames = (post.metadata?.tagged || [])
    .map((id) => memberMap[id]?.name)
    .filter(Boolean) as string[];

  const liveBets = settledLiveBets(post.metadata?.liveBets);
  const totalLiveBetMoney = liveBets.reduce((acc, b) => {
    if (b.status === 'won') return acc + (b.wager_cents || 0) / 100;
    if (b.status === 'lost') return acc - (b.wager_cents || 0) / 100;
    return acc;
  }, 0);

  const games = gamesWithSettlement(post.metadata?.games);
  const gameNote = games.length
    ? ` Side games: ${games.map((g) => g.type).join(', ')}.`
    : '';

  const firstName = player.split(' ')[0];
  const shortCourse = courseName.replace(/^ANCC\s+/i, '');

  const subject = `${firstName} shot ${gross} at ${shortCourse}`;
  const bodyParts = [
    `${player} carded a ${gross} (${diffStr}) at ${courseName}.`,
    highlights ? ` Highlights: ${highlights}.` : '',
    taggedNames.length ? ` Played alongside ${taggedNames.join(', ')}.` : '',
    gameNote,
    totalLiveBetMoney !== 0
      ? ` Net side bets: ${totalLiveBetMoney >= 0 ? '+' : ''}$${Math.abs(totalLiveBetMoney).toFixed(0)}.`
      : '',
    post.body?.trim() ? `\n\n"${post.body.trim()}"` : '',
    '\n\nFull round details available in Bag Tag.',
  ];

  return { subject, body: bodyParts.join('').trim() };
}
